import {
  COST_EMPTY,
  EXIT,
  INF,
  NONE,
  enemyEnterCost,
  enemyGoals,
  flowField,
  neighbors,
  optimalNext,
  soldierEnterCost,
  topRow,
  tracePaths,
  type EnterCost,
  type Field,
  type PathPreview,
} from './path';
import { nextInt } from './rng';
import { defaultSkillMods } from './skills';
import { units } from './state';
import { unitTraits } from './traits';
import type {
  ArchetypeId,
  Balance,
  BossAbility,
  EnemyArchId,
  HeroGearMods,
  HeroId,
  HeroSkill,
  PerkId,
  RunState,
  SkillMods,
  SoldierStats,
  SpawnGroup,
  TraitId,
  Unit,
  UnitStats,
} from './types';

export interface Enemy {
  id: number;
  arch: EnemyArchId;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  speed: number;
  dmg: number;
  interval: number;
  acc: number;
  bounty: number;
  burnDmg: number;
  burnTicks: number;
  burnAcc: number;
  /** Ticks left at half speed (tesla 'shock' trait). */
  slowTicks: number;
  alive: boolean;
  /** Last cell reached. cy = -1 while still above the board. */
  cx: number;
  cy: number;
  hasTarget: boolean;
  tx: number;
  ty: number;
  nextCell: number;
  exiting: boolean;
  atBase: boolean;
  /** Index of the structure this enemy is chewing through, or -1. */
  blocked: number;
  /** Flies over everything: never blocked, never fights soldiers, only shooters and the hero can hit it. */
  flying: boolean;
  /** Flat reduction per hit; burning ignores it. */
  armor: number;
  /** Bosses only: their trick, and the bookkeeping for it. */
  ability: BossAbility | null;
  abilityAcc: number;
  /** Tick of the last damage taken; regen waits for a quiet spell. */
  lastHitTick: number;
  /** Percent of base HP this enemy spawned with; summoned minions inherit it. */
  hpPct: number;
}

export interface Soldier {
  id: number;
  /** Owning spawner unit id, or -1 for the hero. */
  owner: number;
  /** Owning spawner's archetype, for rendering; null for the hero. */
  ownerArch: ArchetypeId | null;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  dmg: number;
  interval: number;
  acc: number;
  speed: number;
  range: number;
  alive: boolean;
  hero: boolean;
  hits: number;
}

export interface Burst {
  x: number;
  y: number;
  radius: number;
}

export interface Structure {
  unit: Unit;
  index: number;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  cd: number;
  healAcc: number;
  spawnAcc: number;
  alive: boolean;
  stats: EffectiveStats;
}

export interface EffectiveStats {
  hp: number;
  dmg: number;
  cooldown: number;
  range: number;
  splashRadius: number;
  burnDmg: number;
  burnTicks: number;
  chainTargets: number;
  chainRadius: number;
  heal: number;
  healInterval: number;
  spawnInterval: number;
  spawnMax: number;
  soldier: SoldierStats | null;
  income: number;
  groundOnly: boolean;
  /** Merge traits active at this level; the fight checks the behavioural ones here. */
  traits: TraitId[];
  /** Damage an attacker takes per blow against this structure (barricade 'spikes'). */
  thorns: number;
}

export interface Shot {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  /** The building that fired, or the spawner whose rifleman fired; 'archery' for the hero's shots. */
  arch: ArchetypeId;
  /** Buildings shoot 'unit'; ranged soldiers 'soldier'; a ranged hero 'hero'. */
  kind: 'unit' | 'soldier' | 'hero';
  splashRadius: number;
  dmg: number;
}

export interface Death {
  x: number;
  y: number;
  arch: EnemyArchId;
  /** Gold the kill paid, for the "+N" floater. */
  bounty: number;
}

export type HitSource = 'shot' | 'burn' | 'melee' | 'crit' | 'pierce';

/** One blow landing somewhere during a step(); the renderer turns these into flashes, numbers and shakes. */
export type Hit =
  | { target: 'enemy'; id: number; x: number; y: number; amount: number; source: HitSource }
  | { target: 'structure'; index: number; amount: number }
  | { target: 'wall'; amount: number }
  | { target: 'soldier'; id: number; x: number; y: number; amount: number; hero: boolean };

export interface SoldierDeath {
  x: number;
  y: number;
  owner: number;
  hero: boolean;
}

export interface StructureLoss {
  index: number;
  arch: ArchetypeId;
}

interface PendingSpawn {
  tick: number;
  order: number;
  arch: EnemyArchId;
  hpPct: number;
  ability?: BossAbility;
}

/** A boss used its trick during the last step(); the renderer shows it. */
export interface AbilityEvent {
  x: number;
  y: number;
  ability: BossAbility;
}

export interface Mods {
  rapidDmgPct: number;
  splashRadiusPct: number;
  burnTicksPct: number;
  chainTargetsAdd: number;
  rangeAdd: number;
  repairPct: number;
  barricadeHpPct: number;
  spawnIntervalPct: number;
  soldierCapAdd: number;
}

export function computeMods(perks: readonly PerkId[]): Mods {
  const m: Mods = {
    rapidDmgPct: 100,
    splashRadiusPct: 100,
    burnTicksPct: 100,
    chainTargetsAdd: 0,
    rangeAdd: 0,
    repairPct: 100,
    barricadeHpPct: 100,
    spawnIntervalPct: 100,
    soldierCapAdd: 0,
  };
  for (const p of perks) {
    if (p === 'rapid_dmg') m.rapidDmgPct += 25;
    else if (p === 'splash_radius') m.splashRadiusPct += 40;
    else if (p === 'burn_duration') m.burnTicksPct += 50;
    else if (p === 'chain_targets') m.chainTargetsAdd += 1;
    else if (p === 'range_all') m.rangeAdd += 1000;
    else if (p === 'repair_boost') m.repairPct += 100;
    else if (p === 'barricade_hp') m.barricadeHpPct += 50;
    else if (p === 'barracks_fast') m.spawnIntervalPct -= 30;
    else if (p === 'army_cap') m.soldierCapAdd += 3;
  }
  return m;
}

function levelMult(balance: Balance, level: number): number {
  const m = balance.levelMult[level - 1];
  if (m === undefined) throw new Error(`no level multiplier for level ${level}`);
  return m;
}

/** Percent multiplier for a card level from the profile; out-of-range levels clamp to the table. */
export function cardMultAt(balance: Balance, level: number): number {
  const i = Math.min(Math.max(1, level), balance.cardMult.length) - 1;
  return balance.cardMult[i] ?? 100;
}

export function heroMultAt(balance: Balance, level: number): number {
  const i = Math.min(Math.max(1, level), balance.heroMult.length) - 1;
  return balance.heroMult[i] ?? 100;
}

export interface HeroEffective {
  hp: number;
  dmg: number;
  speed: number;
  interval: number;
  range: number;
  /** Skill with gear folded in: cadence from the set bonus, shockwave radius from the charm. */
  skill: HeroSkill;
}

/** A hero as it enters the field: base × level multiplier × gear, all integer percents. */
export function heroStats(balance: Balance, heroId: HeroId, level: number, gear: HeroGearMods, skills?: SkillMods): HeroEffective {
  const h = balance.heroes[heroId];
  const mult = heroMultAt(balance, level);
  const sp = skills ?? defaultSkillMods();
  const scaled = (base: number, pct: number): number => Math.floor((Math.floor((Math.floor((base * mult) / 100) * pct) / 100) * sp.heroPct) / 100);
  const every = Math.max(2, h.skill.every + gear.aoeEveryDelta);
  let skill: HeroSkill;
  if (h.skill.kind === 'shockwave') skill = { kind: 'shockwave', every, radius: Math.floor((h.skill.radius * gear.aoePct) / 100) };
  else if (h.skill.kind === 'mend') skill = { kind: 'mend', every, wallHeal: scaled(h.skill.wallHeal, gear.aoePct), structureHeal: scaled(h.skill.structureHeal, gear.aoePct) };
  else skill = { kind: 'snipe', every, pct: Math.floor((h.skill.pct * gear.aoePct) / 100) };
  return {
    hp: scaled(h.hp, gear.hpPct),
    dmg: scaled(h.dmg, gear.dmgPct),
    speed: Math.floor((Math.floor((h.speed * gear.speedPct) / 100) * sp.heroSpeedPct) / 100),
    interval: h.interval,
    range: h.range,
    skill,
  };
}

/** Stats of a building at its merge level, with the profile's card level folded in. */
export function effectiveStats(unit: Unit, balance: Balance, mods: Mods, cardLevel = 1, skills?: SkillMods): EffectiveStats {
  const base: UnitStats = balance.units[unit.arch];
  const sk = skills ?? defaultSkillMods();
  const mult = Math.floor((levelMult(balance, unit.level) * cardMultAt(balance, cardLevel)) / 100);
  const traits = unitTraits(unit, balance);
  const has = (t: TraitId): boolean => traits.includes(t);
  let dmg = Math.floor((base.dmg * mult) / 100);
  if (unit.arch === 'rapid') dmg = Math.floor((dmg * mods.rapidDmgPct) / 100);
  const attacker = base.cooldown > 0;
  if (attacker) dmg = Math.floor((dmg * sk.turretDmgPct) / 100);
  let hp = Math.floor((base.hp * mult) / 100);
  if (unit.arch === 'barricade') hp = Math.floor((hp * mods.barricadeHpPct) / 100);
  hp = Math.floor((hp * sk.structureHpPct) / 100);
  const soldier: SoldierStats | null = base.soldier
    ? {
        hp: Math.floor((Math.floor((Math.floor((base.soldier.hp * mult) / 100) * sk.soldierPct) / 100) * (has('veterans') ? 150 : 100)) / 100),
        dmg: Math.floor((Math.floor((Math.floor((base.soldier.dmg * mult) / 100) * sk.soldierPct) / 100) * (has('charge') ? 150 : 100)) / 100),
        interval: base.soldier.interval,
        speed: base.soldier.speed,
        range: base.soldier.range + (has('longbow') ? 700 : 0),
      }
    : null;
  const extraAlive = has('squad') || has('volley') || has('pair') ? 1 : 0;
  return {
    hp,
    dmg,
    cooldown: base.cooldown,
    range: attacker ? base.range + mods.rangeAdd + sk.rangeAdd : 0,
    splashRadius: base.splashRadius ? Math.floor((Math.floor((base.splashRadius * mods.splashRadiusPct) / 100) * (has('wide') ? 150 : 100)) / 100) : 0,
    burnDmg: base.burnDmg ? Math.floor((Math.floor((base.burnDmg * mult) / 100) * sk.turretDmgPct) / 100) * (has('inferno') ? 2 : 1) : 0,
    burnTicks: base.burnTicks ? Math.floor((base.burnTicks * mods.burnTicksPct) / 100) : 0,
    chainTargets: base.chainTargets ? base.chainTargets + mods.chainTargetsAdd + (has('fork') ? 2 : 0) : 0,
    chainRadius: base.chainRadius ?? 0,
    heal: base.heal ? Math.floor((base.heal * mult * mods.repairPct) / 10000) : 0,
    healInterval: base.healInterval ? (has('fast_heal') ? Math.max(5, base.healInterval >> 1) : base.healInterval) : 0,
    spawnInterval: base.spawnInterval ? Math.max(10, Math.floor((base.spawnInterval * mods.spawnIntervalPct) / 100)) : 0,
    // One more soldier every two levels, plus one from the squad / volley / pair trait.
    spawnMax: base.spawnMax ? base.spawnMax + Math.floor((unit.level - 1) / 2) + extraAlive : 0,
    soldier,
    income: base.income ? Math.floor((Math.floor((Math.floor((base.income * mult) / 100) * sk.minePct) / 100) * (has('vault') ? 150 : 100)) / 100) : 0,
    groundOnly: (base.groundOnly ?? false) && !has('flak'),
    traits,
    thorns: has('spikes') ? Math.max(1, Math.floor(levelMult(balance, unit.level) / 100)) : 0,
  };
}

function dist2(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/** Manhattan-style integer movement: x first, then y. Returns true when the target is reached. */
function moveToward(o: { x: number; y: number }, tx: number, ty: number, speed: number): boolean {
  let budget = speed;
  const dx = tx - o.x;
  if (dx !== 0) {
    const step = Math.min(Math.abs(dx), budget);
    o.x += dx > 0 ? step : -step;
    budget -= step;
  }
  const dy = ty - o.y;
  if (dy !== 0 && budget > 0) {
    const step = Math.min(Math.abs(dy), budget);
    o.y += dy > 0 ? step : -step;
  }
  return o.x === tx && o.y === ty;
}

/**
 * One wave of auto-battle on the shared grid. Deterministic given the RunState (including its rng).
 * Step it tick by tick on the client for rendering; run it to the end on the server.
 * Destroyed structures leave state.grid for the rest of the wave (paths open up) and are rebuilt
 * by finishBattle once the wave is cleared, like in the reference game.
 */
export class BattleSim {
  readonly enemies: Enemy[] = [];
  readonly soldiers: Soldier[] = [];
  readonly structures = new Map<number, Structure>();
  private pending: PendingSpawn[];
  private nextEnemyId = 1;
  private nextSoldierId = 1;
  tick = 0;
  wallHp: number;
  readonly wallMax: number;
  done = false;
  cleared = false;
  kills = 0;
  gold = 0;
  lostStructures = 0;
  /** Structures broken this wave; finishBattle puts them back (the original respawns buildings after a wave). */
  readonly destroyed: { index: number; unit: Unit }[] = [];
  /** Total HP of the whole wave and how much of it is gone, for the wave bar. */
  waveHpTotal = 0;
  waveHpDealt = 0;
  lastShots: Shot[] = [];
  lastDeaths: Death[] = [];
  lastStructureLosses: StructureLoss[] = [];
  /** Every blow of the last step(), presentation only; nothing here feeds back into the state. */
  lastHits: Hit[] = [];
  lastSoldierDeaths: SoldierDeath[] = [];
  /** Soldiers (and the hero) that walked out during the last step(). */
  lastSoldierSpawns: { x: number; y: number; hero: boolean }[] = [];
  lastAbilities: AbilityEvent[] = [];
  /** Hero shockwaves during the last step(), for rendering. */
  lastBursts: Burst[] = [];
  readonly W: number;
  readonly H: number;
  enemyField!: Field;
  soldierField!: Field;
  /** Flying enemies ignore the maze: their field treats every non-base cell as open ground. */
  private flyField!: Field;
  private flyCost!: EnterCost;
  private enemyCost!: EnterCost;
  /** Soldiers (hero excluded) allowed alive at once this run. */
  readonly soldierCap: number;
  /** Current enemy routes, for rendering. Refreshed whenever the board changes. */
  paths!: PathPreview;
  private dirty = false;

  constructor(
    private readonly state: RunState,
    private readonly balance: Balance,
    waveSpawns: readonly SpawnGroup[],
  ) {
    this.W = state.width;
    this.H = state.height;
    this.wallHp = state.wallHp;
    this.wallMax = state.wallMax;
    const mods = computeMods(state.perks);
    this.soldierCap = balance.soldierCap + mods.soldierCapAdd;
    for (const { cell, unit } of units(state)) {
      const stats = effectiveStats(unit, balance, mods, state.cardLevels[unit.arch], state.skills);
      const index = cell.y * this.W + cell.x;
      this.structures.set(index, {
        unit,
        index,
        x: cell.x * 1000 + 500,
        y: cell.y * 1000 + 500,
        hp: stats.hp,
        maxHp: stats.hp,
        cd: 0,
        healAcc: 0,
        spawnAcc: 0,
        alive: true,
        stats,
      });
    }
    this.pending = [];
    let order = 0;
    // Challenge modifiers: swap archetypes and scale HP before anything spawns.
    const cm = state.modifiers;
    const tough = cm.includes('tough_enemies');
    this.fastEnemies = cm.includes('fast_enemies');
    for (const g of waveSpawns) {
      let arch = g.arch;
      if (cm.includes('air_raid') && arch === 'runner') arch = 'flyer';
      if (cm.includes('iron_horde') && arch === 'walker') arch = 'armored';
      const hpPct = tough ? Math.floor((g.hpPct * 150) / 100) : g.hpPct;
      const hp = Math.max(1, Math.floor((balance.enemies[arch].hp * hpPct) / 100));
      this.waveHpTotal += hp * g.count;
      for (let i = 0; i < g.count; i++) {
        const ps: PendingSpawn = { tick: g.startTick + g.spacing * i, order: order++, arch, hpPct };
        if (arch === 'boss' && g.ability) ps.ability = g.ability;
        this.pending.push(ps);
      }
    }
    this.pending.sort((a, b) => a.tick - b.tick || a.order - b.order);
    this.recomputeFields();
    if (state.heroLevel > 0) this.spawnHero(state.heroLevel);
  }

  /** This run's hero skill with gear folded in; set when the hero spawns. */
  private heroSkill: HeroSkill | null = null;
  private fastEnemies = false;

  /** The hero walks out of the bunker: first empty cell touching the base, else the bottom row centre. */
  private spawnHero(level: number): void {
    const st = heroStats(this.balance, this.state.heroId, level, this.state.heroGear, this.state.skills);
    this.heroSkill = st.skill;
    let cell = -1;
    for (const g of enemyGoals(this.state.grid, this.W, this.H)) {
      if (this.state.grid[g]!.kind === 'empty') {
        cell = g;
        break;
      }
    }
    if (cell < 0) {
      const mid = Math.floor(this.W / 2);
      for (let d = 0; d < this.W && cell < 0; d++) {
        for (const x of [mid - d, mid + d]) {
          if (x < 0 || x >= this.W) continue;
          const i = (this.H - 1) * this.W + x;
          if (this.state.grid[i]!.kind === 'empty') {
            cell = i;
            break;
          }
        }
      }
    }
    if (cell < 0) return;
    this.lastSoldierSpawns.push({ x: (cell % this.W) * 1000 + 500, y: Math.floor(cell / this.W) * 1000 + 500, hero: true });
    this.soldiers.push({
      id: this.nextSoldierId++,
      owner: -1,
      ownerArch: null,
      x: (cell % this.W) * 1000 + 500,
      y: Math.floor(cell / this.W) * 1000 + 500,
      hp: st.hp,
      maxHp: st.hp,
      dmg: st.dmg,
      interval: st.interval,
      acc: 0,
      speed: st.speed,
      range: st.range,
      alive: true,
      hero: true,
      hits: 0,
    });
  }

  private structureAlive = (i: number): boolean => {
    const s = this.structures.get(i);
    return s !== undefined && s.alive;
  };

  private recomputeFields(): void {
    const ecost = enemyEnterCost(this.state.grid, this.structureAlive);
    this.enemyCost = ecost;
    const goals = enemyGoals(this.state.grid, this.W, this.H);
    this.enemyField = flowField(this.W, this.H, goals, ecost);
    if (!this.flyField) {
      // Rocks and buildings do not exist for flyers; only the base itself is solid. Never changes mid-battle.
      const grid = this.state.grid;
      this.flyCost = (i: number): number => (grid[i]!.kind === 'base' ? INF : COST_EMPTY);
      this.flyField = flowField(this.W, this.H, goals, this.flyCost);
    }
    this.soldierField = flowField(this.W, this.H, topRow(this.W), soldierEnterCost(this.state.grid, this.structureAlive));
    this.paths = tracePaths(this.W, this.H, this.enemyField, ecost, this.structureAlive);
    this.dirty = false;
  }

  private spawnEnemy(p: PendingSpawn): void {
    const cols = this.paths.spawnColumns.length > 0 ? this.paths.spawnColumns : topRow(this.W);
    const col = cols[nextInt(this.state.rng, cols.length)]!;
    const jitter = nextInt(this.state.rng, 401) - 200;
    this.spawnAt(p.arch, p.hpPct, col * 1000 + 500 + jitter, -500, col, -1, p.ability ?? null);
  }

  private spawnAt(arch: EnemyArchId, hpPct: number, x: number, y: number, cx: number, cy: number, ability: BossAbility | null): Enemy {
    const es = this.balance.enemies[arch];
    const hp = Math.max(1, Math.floor((es.hp * hpPct) / 100));
    const e: Enemy = {
      id: this.nextEnemyId++,
      arch,
      x,
      y,
      hp,
      maxHp: hp,
      speed: this.fastEnemies ? Math.floor((es.speed * 130) / 100) : es.speed,
      dmg: es.attackDmg,
      interval: es.attackInterval,
      acc: 0,
      bounty: es.bounty,
      burnDmg: 0,
      burnTicks: 0,
      slowTicks: 0,
      burnAcc: 0,
      alive: true,
      cx,
      cy,
      hasTarget: false,
      tx: 0,
      ty: 0,
      nextCell: -1,
      flying: es.flying ?? false,
      armor: es.armor ?? 0,
      exiting: false,
      atBase: false,
      blocked: -1,
      ability,
      abilityAcc: 0,
      lastHitTick: -1000,
      hpPct,
    };
    this.enemies.push(e);
    return e;
  }

  private damageEnemy(e: Enemy, amount: number, source: HitSource = 'shot'): void {
    if (!e.alive) return;
    // Armour takes a flat bite out of every ordinary hit; fire and the sniper's marked round go through.
    if (e.armor > 0 && source !== 'burn' && source !== 'crit' && source !== 'pierce') amount = Math.max(1, amount - e.armor);
    this.waveHpDealt += Math.min(amount, e.hp);
    e.hp -= amount;
    e.lastHitTick = this.tick;
    this.lastHits.push({ target: 'enemy', id: e.id, x: e.x, y: e.y, amount, source });
    if (e.hp <= 0) {
      e.hp = 0;
      e.alive = false;
      this.kills++;
      this.gold += e.bounty;
      this.lastDeaths.push({ x: e.x, y: e.y, arch: e.arch, bounty: e.bounty });
    }
  }

  private damageStructure(s: Structure, amount: number): void {
    if (!s.alive) return;
    s.hp -= amount;
    this.lastHits.push({ target: 'structure', index: s.index, amount });
    if (s.hp <= 0) {
      s.hp = 0;
      s.alive = false;
      this.lostStructures++;
      this.destroyed.push({ index: s.index, unit: s.unit });
      this.state.grid[s.index] = { kind: 'empty' };
      this.lastStructureLosses.push({ index: s.index, arch: s.unit.arch });
      this.dirty = true;
    }
  }

  /** Enemy closest to the base (largest y) within range; ties broken by id. */
  private pickTarget(sx: number, sy: number, range: number, exclude?: Set<number>, groundOnly = false): Enemy | null {
    let best: Enemy | null = null;
    const r2 = range * range;
    for (const e of this.enemies) {
      if (!e.alive) continue;
      if (groundOnly && e.flying) continue;
      if (exclude && exclude.has(e.id)) continue;
      if (dist2(sx, sy, e.x, e.y) > r2) continue;
      if (!best || e.y > best.y || (e.y === best.y && e.id < best.id)) best = e;
    }
    return best;
  }

  private fire(s: Structure): void {
    const t = s.stats;
    const target = this.pickTarget(s.x, s.y, t.range, undefined, t.groundOnly);
    if (!target) return;
    s.cd = t.cooldown;
    this.lastShots.push({
      fromX: s.x,
      fromY: s.y,
      toX: target.x,
      toY: target.y,
      arch: s.unit.arch,
      kind: 'unit',
      splashRadius: t.splashRadius,
      dmg: t.dmg,
    });
    if (t.splashRadius > 0) {
      const r2 = t.splashRadius * t.splashRadius;
      const tx = target.x;
      const ty = target.y;
      for (const e of this.enemies) {
        if (e.alive && (!t.groundOnly || !e.flying) && dist2(tx, ty, e.x, e.y) <= r2) this.damageEnemy(e, t.dmg);
      }
      return;
    }
    if (t.chainTargets > 0) {
      const hit = new Set<number>();
      let cur: Enemy | null = target;
      const cr2 = t.chainRadius * t.chainRadius;
      const shock = t.traits.includes('shock');
      for (let k = 0; k < t.chainTargets && cur; k++) {
        hit.add(cur.id);
        this.damageEnemy(cur, t.dmg);
        if (shock && cur.alive) cur.slowTicks = 20;
        let next: Enemy | null = null;
        for (const e of this.enemies) {
          if (!e.alive || hit.has(e.id)) continue;
          if (dist2(cur.x, cur.y, e.x, e.y) > cr2) continue;
          if (!next || e.y > next.y || (e.y === next.y && e.id < next.id)) next = e;
        }
        cur = next;
      }
      return;
    }
    const source: HitSource = t.traits.includes('pierce') ? 'pierce' : 'shot';
    this.damageEnemy(target, t.dmg, source);
    if (t.burnTicks > 0) {
      this.ignite(target, t);
      // Spreading flame: whoever stands next to the target catches fire too.
      if (t.traits.includes('spread')) {
        for (const e of this.enemies) {
          if (e.alive && e.id !== target.id && dist2(target.x, target.y, e.x, e.y) <= 700 * 700) this.ignite(e, t);
        }
      }
    }
    // Twin barrels: the same shot also finds a second enemy in range.
    if (t.traits.includes('twin')) {
      const second = this.pickTarget(s.x, s.y, t.range, new Set([target.id]), t.groundOnly);
      if (second) {
        this.lastShots.push({ fromX: s.x, fromY: s.y, toX: second.x, toY: second.y, arch: s.unit.arch, kind: 'unit', splashRadius: 0, dmg: t.dmg });
        this.damageEnemy(second, t.dmg, source);
      }
    }
  }

  private ignite(e: Enemy, t: EffectiveStats): void {
    if (!e.alive) return;
    if (t.burnDmg >= e.burnDmg) {
      e.burnDmg = t.burnDmg;
      e.burnTicks = t.burnTicks;
    } else if (e.burnTicks < t.burnTicks) {
      e.burnTicks = t.burnTicks;
    }
  }

  private nearestSoldier(x: number, y: number, range: number): Soldier | null {
    let best: Soldier | null = null;
    let bestD = range * range + 1;
    for (const s of this.soldiers) {
      if (!s.alive) continue;
      const d = dist2(x, y, s.x, s.y);
      if (d < bestD || (d === bestD && best && s.id < best.id)) {
        best = s;
        bestD = d;
      }
    }
    return best;
  }

  /** Can this soldier hurt that enemy? Flyers are out of reach for melee fighters; riflemen and the hero manage. */
  private canReach(s: Soldier, e: Enemy): boolean {
    return !e.flying || s.hero || s.range >= 1500;
  }

  private nearestEnemy(s: Soldier): { e: Enemy; d2: number } | null {
    const x = s.x;
    const y = s.y;
    let best: Enemy | null = null;
    let bestD = INF;
    for (const e of this.enemies) {
      if (!e.alive || !this.canReach(s, e)) continue;
      const d = dist2(x, y, e.x, e.y);
      if (d < bestD || (d === bestD && best && e.id < best.id)) {
        best = e;
        bestD = d;
      }
    }
    return best ? { e: best, d2: bestD } : null;
  }

  /** Boss tricks. Summon: two runners at its feet every 4 s. Regen: 2 % of max HP every second after 2 s unhurt. Smash is applied where it hits buildings. */
  private stepAbility(e: Enemy): void {
    if (!e.ability) return;
    e.abilityAcc++;
    if (e.ability === 'summon' && e.abilityAcc >= 80) {
      e.abilityAcc = 0;
      let alive = 0;
      for (const o of this.enemies) if (o.alive) alive++;
      if (alive >= 60) return;
      for (let k = 0; k < 2; k++) {
        const m = this.spawnAt('runner', e.hpPct, e.x + (k === 0 ? -250 : 250), e.y, e.cx, e.cy, null);
        this.waveHpTotal += m.maxHp;
      }
      this.lastAbilities.push({ x: e.x, y: e.y, ability: 'summon' });
    } else if (e.ability === 'regen' && e.abilityAcc >= 20) {
      e.abilityAcc = 0;
      if (e.hp < e.maxHp && this.tick - e.lastHitTick >= 40) {
        const heal = Math.max(1, Math.floor((e.maxHp * 2) / 100));
        e.hp = Math.min(e.maxHp, e.hp + heal);
        this.waveHpDealt = Math.max(0, this.waveHpDealt - heal);
        this.lastAbilities.push({ x: e.x, y: e.y, ability: 'regen' });
      }
    }
  }

  private stepEnemy(e: Enemy): void {
    this.stepAbility(e);
    if (e.slowTicks > 0) e.slowTicks--;
    if (e.burnTicks > 0) {
      e.burnTicks--;
      e.burnAcc++;
      if (e.burnAcc >= 10) {
        e.burnAcc = 0;
        this.damageEnemy(e, e.burnDmg, 'burn');
        if (!e.alive) return;
      }
    }
    const strike = (): boolean => {
      e.acc++;
      if (e.acc >= e.interval) {
        e.acc = 0;
        return true;
      }
      return false;
    };
    // Soldiers in reach come first: they exist to stop enemies. Flyers pass over them.
    const soldier = e.flying ? null : this.nearestSoldier(e.x, e.y, this.balance.enemyMeleeRange);
    if (soldier) {
      if (strike()) {
        soldier.hp -= e.dmg;
        this.lastHits.push({ target: 'soldier', id: soldier.id, x: soldier.x, y: soldier.y, amount: e.dmg, hero: soldier.hero });
        if (soldier.hp <= 0) {
          soldier.hp = 0;
          soldier.alive = false;
          this.lastSoldierDeaths.push({ x: soldier.x, y: soldier.y, owner: soldier.owner, hero: soldier.hero });
        }
      }
      return;
    }
    if (e.atBase) {
      if (strike()) {
        this.wallHp -= e.dmg;
        this.lastHits.push({ target: 'wall', amount: e.dmg });
      }
      return;
    }
    if (e.blocked >= 0) {
      const s = this.structures.get(e.blocked);
      if (s && s.alive) {
        if (strike()) {
          // Smasher: buildings in its way crumble four times faster.
          const smash = e.ability === 'smash';
          this.damageStructure(s, smash ? e.dmg * 4 : e.dmg);
          if (smash) this.lastAbilities.push({ x: e.x, y: e.y, ability: 'smash' });
          // Spiked barricade: every blow against it costs the attacker.
          if (s.stats.thorns > 0) this.damageEnemy(e, s.stats.thorns, 'melee');
        }
        return;
      }
      e.blocked = -1;
      e.hasTarget = false;
    }
    if (!e.hasTarget) {
      const cur = e.cy < 0 ? -1 : e.cy * this.W + e.cx;
      let nextIdx: number;
      if (cur === -1) nextIdx = e.cx;
      else {
        // Every equally cheap continuation is fair game: the horde spreads instead of walking in single file.
        const opts = e.flying
          ? optimalNext(this.flyField, this.flyCost, this.W, this.H, cur)
          : optimalNext(this.enemyField, this.enemyCost, this.W, this.H, cur);
        if (opts.length === 0) return; // Boxed in with nowhere to go; cannot happen while structures are breakable.
        nextIdx = opts.length === 1 ? opts[0]! : opts[nextInt(this.state.rng, opts.length)]!;
      }
      if (nextIdx === EXIT) {
        const t = this.exitTarget(cur);
        e.tx = t.x;
        e.ty = t.y;
        e.exiting = true;
        e.nextCell = -1;
      } else {
        const s = this.structures.get(nextIdx);
        if (s && s.alive && !e.flying) {
          e.blocked = nextIdx;
          return;
        }
        e.tx = (nextIdx % this.W) * 1000 + 500;
        e.ty = Math.floor(nextIdx / this.W) * 1000 + 500;
        e.exiting = false;
        e.nextCell = nextIdx;
      }
      e.hasTarget = true;
    }
    if (moveToward(e, e.tx, e.ty, e.slowTicks > 0 ? Math.max(1, e.speed >> 1) : e.speed)) {
      if (e.exiting) e.atBase = true;
      else {
        e.cx = e.nextCell % this.W;
        e.cy = Math.floor(e.nextCell / this.W);
      }
      e.hasTarget = false;
    }
  }

  /** Where an enemy standing on goal cell `cur` walks to hit the base: a third of the way into the base building, or the bottom edge. */
  private exitTarget(cur: number): { x: number; y: number } {
    const cx = (cur % this.W) * 1000 + 500;
    const cy = Math.floor(cur / this.W) * 1000 + 500;
    for (const ni of neighbors(this.W, this.H, cur)) {
      if (this.state.grid[ni]!.kind === 'base') {
        const bx = (ni % this.W) * 1000 + 500;
        const by = Math.floor(ni / this.W) * 1000 + 500;
        return { x: cx + Math.trunc((bx - cx) * 0.35), y: cy + Math.trunc((by - cy) * 0.35) };
      }
    }
    return { x: cx, y: this.H * 1000 - 150 };
  }

  private cellOf(x: number, y: number): number {
    const cx = Math.min(this.W - 1, Math.max(0, Math.floor(x / 1000)));
    const cy = Math.min(this.H - 1, Math.max(0, Math.floor(y / 1000)));
    return cy * this.W + cx;
  }

  /** First step of the shortest passable path from one cell to another (BFS, fixed neighbour order). -1 if none. */
  private soldierStep(from: number, to: number): number {
    if (from === to) return to;
    const passable = soldierEnterCost(this.state.grid, this.structureAlive);
    const prev = new Array<number>(this.W * this.H).fill(-2);
    prev[from] = -1;
    const queue = [from];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      for (const n of neighbors(this.W, this.H, cur)) {
        if (prev[n] !== -2) continue;
        // The target cell itself may be blocked (an enemy chewing on a wall): stop next to it.
        if (n !== to && passable(n) >= INF) continue;
        prev[n] = cur;
        if (n === to) {
          let step = n;
          while (prev[step] !== from) step = prev[step]!;
          return step;
        }
        queue.push(n);
      }
    }
    return -1;
  }

  private stepSoldier(s: Soldier): void {
    const near = this.nearestEnemy(s);
    if (!near) {
      // Nobody to fight yet: hold position at the cell centre.
      const cur = this.cellOf(s.x, s.y);
      moveToward(s, (cur % this.W) * 1000 + 500, Math.floor(cur / this.W) * 1000 + 500, s.speed);
      return;
    }
    if (near.d2 <= s.range * s.range) {
      s.acc++;
      if (s.acc >= s.interval) {
        s.acc = 0;
        const ranged = s.range >= 1500;
        const sk = s.hero ? this.heroSkill : null;
        if (s.hero) s.hits++;
        // The sniper's marked round: the N-th shot is the skill itself, so it lands before the others fire.
        const crit = sk !== null && sk.kind === 'snipe' && sk.every > 0 && s.hits % sk.every === 0;
        const dmg = crit ? Math.floor((s.dmg * (sk as { pct: number }).pct) / 100) : s.dmg;
        if (ranged) {
          this.lastShots.push({ fromX: s.x, fromY: s.y, toX: near.e.x, toY: near.e.y, arch: s.ownerArch ?? 'archery', kind: s.hero ? 'hero' : 'soldier', splashRadius: 0, dmg });
        }
        this.damageEnemy(near.e, dmg, crit ? 'crit' : 'melee');
        if (s.hero) {
          if (sk && sk.every > 0 && sk.kind !== 'snipe' && s.hits % sk.every === 0) {
            if (sk.kind === 'shockwave') {
              const r2 = sk.radius * sk.radius;
              for (const e of this.enemies) {
                if (e.alive && dist2(s.x, s.y, e.x, e.y) <= r2) this.damageEnemy(e, s.dmg, 'melee');
              }
              this.lastBursts.push({ x: s.x, y: s.y, radius: sk.radius });
            } else {
              // Mend: patch the bunker and the most battered standing building.
              this.wallHp = Math.min(this.wallMax, this.wallHp + sk.wallHeal);
              let worst: Structure | null = null;
              for (const st of this.structures.values()) {
                if (!st.alive || st.hp >= st.maxHp) continue;
                if (!worst || st.hp * worst.maxHp < worst.hp * st.maxHp) worst = st;
              }
              if (worst) worst.hp = Math.min(worst.maxHp, worst.hp + sk.structureHeal);
              this.lastBursts.push({ x: s.x, y: s.y, radius: 600 });
            }
          }
        }
      }
      return;
    }
    if (near.d2 <= 1500 * 1500) {
      // Close enough to close in directly.
      moveToward(s, near.e.x, near.e.y, s.speed);
      return;
    }
    // Chase through the maze: enemies still above the board are approached via the cell under them.
    const from = this.cellOf(s.x, s.y);
    const to = near.e.cy < 0 ? Math.min(this.W - 1, Math.max(0, near.e.cx)) : this.cellOf(near.e.x, near.e.y);
    const step = this.soldierStep(from, to);
    if (step < 0) {
      moveToward(s, (from % this.W) * 1000 + 500, Math.floor(from / this.W) * 1000 + 500, s.speed);
      return;
    }
    const passable = soldierEnterCost(this.state.grid, this.structureAlive)(step) < INF;
    if (!passable) {
      // Only the enemy's own (blocked) cell is left: fight from where we stand.
      return;
    }
    moveToward(s, (step % this.W) * 1000 + 500, Math.floor(step / this.W) * 1000 + 500, s.speed);
  }

  private trySpawnSoldier(s: Structure): void {
    const st = s.stats;
    if (!st.soldier || st.spawnInterval <= 0) return;
    s.spawnAcc++;
    if (s.spawnAcc < st.spawnInterval) return;
    let alive = 0;
    let total = 0;
    for (const so of this.soldiers) {
      if (!so.alive || so.hero) continue;
      total++;
      if (so.owner === s.unit.id) alive++;
    }
    if (alive >= st.spawnMax || total >= this.soldierCap) return;
    const cx = s.index % this.W;
    const cy = Math.floor(s.index / this.W);
    // Prefer the neighbour closest to the front (smallest soldier-field distance); fixed order for ties.
    const nbs = [
      [cx, cy - 1],
      [cx - 1, cy],
      [cx + 1, cy],
      [cx, cy + 1],
    ] as const;
    let bestIdx = -1;
    let bestDist = INF;
    for (const [nx, ny] of nbs) {
      if (nx < 0 || ny < 0 || nx >= this.W || ny >= this.H) continue;
      const ni = ny * this.W + nx;
      if (this.state.grid[ni]!.kind !== 'empty') continue;
      const d = this.soldierField.dist[ni]!;
      if (d < bestDist) {
        bestDist = d;
        bestIdx = ni;
      }
    }
    if (bestIdx < 0) return;
    s.spawnAcc = 0;
    this.lastSoldierSpawns.push({ x: (bestIdx % this.W) * 1000 + 500, y: Math.floor(bestIdx / this.W) * 1000 + 500, hero: false });
    this.soldiers.push({
      id: this.nextSoldierId++,
      owner: s.unit.id,
      ownerArch: s.unit.arch,
      x: (bestIdx % this.W) * 1000 + 500,
      y: Math.floor(bestIdx / this.W) * 1000 + 500,
      hp: st.soldier.hp,
      maxHp: st.soldier.hp,
      dmg: st.soldier.dmg,
      interval: st.soldier.interval,
      acc: 0,
      speed: st.soldier.speed,
      range: st.soldier.range,
      alive: true,
      hero: false,
      hits: 0,
    });
  }

  step(): void {
    if (this.done) return;
    this.lastShots = [];
    this.lastDeaths = [];
    this.lastStructureLosses = [];
    this.lastBursts = [];
    this.lastHits = [];
    this.lastSoldierDeaths = [];
    this.lastSoldierSpawns = [];
    this.lastAbilities = [];
    const tick = this.tick;

    while (this.pending.length > 0 && this.pending[0]!.tick <= tick) {
      this.spawnEnemy(this.pending.shift()!);
    }

    for (const e of this.enemies) {
      if (e.alive) this.stepEnemy(e);
    }
    for (const s of this.soldiers) {
      if (s.alive) this.stepSoldier(s);
    }
    for (const s of this.structures.values()) {
      if (!s.alive) continue;
      const t = s.stats;
      if (t.heal > 0 && t.healInterval > 0) {
        s.healAcc++;
        if (s.healAcc >= t.healInterval) {
          s.healAcc = 0;
          this.wallHp = Math.min(this.wallMax, this.wallHp + t.heal);
          // Repair crew: the most battered standing building gets patched as well.
          if (t.traits.includes('crew')) {
            let worst: Structure | null = null;
            for (const st of this.structures.values()) {
              if (!st.alive || st.hp >= st.maxHp) continue;
              if (!worst || st.hp * worst.maxHp < worst.hp * st.maxHp) worst = st;
            }
            if (worst) worst.hp = Math.min(worst.maxHp, worst.hp + t.heal * 3);
          }
        }
      } else if (t.traits.includes('regen') && s.hp < s.maxHp) {
        // Regrowing barricade: 2 % of its HP back every second while it stands.
        s.healAcc++;
        if (s.healAcc >= 20) {
          s.healAcc = 0;
          s.hp = Math.min(s.maxHp, s.hp + Math.max(1, Math.floor((s.maxHp * 2) / 100)));
        }
      }
      if (t.soldier) this.trySpawnSoldier(s);
      if (t.cooldown <= 0) continue;
      if (s.cd > 0) {
        s.cd--;
        continue;
      }
      this.fire(s);
    }

    if (this.dirty) {
      this.recomputeFields();
      for (const e of this.enemies) {
        if (!e.alive || e.atBase) continue;
        e.hasTarget = false;
        e.blocked = -1;
      }
    }

    this.tick++;

    if (this.wallHp <= 0) {
      this.wallHp = 0;
      this.done = true;
      this.cleared = false;
      return;
    }
    const anyAlive = this.enemies.some((e) => e.alive);
    if (!anyAlive && this.pending.length === 0) {
      this.done = true;
      this.cleared = true;
      return;
    }
    if (this.tick >= this.balance.maxBattleTicks) {
      this.done = true;
      this.cleared = false;
    }
  }

  runToEnd(): void {
    while (!this.done) this.step();
  }
}
